"use client";

import React, { createContext, useContext, useState, useCallback } from "react";

interface ListContextType {
    revalidatingLists: Set<string>;
    setRevalidatingLists: React.Dispatch<React.SetStateAction<Set<string>>>;
    clearRevalidatingList: (listId: string) => void;
    addRevalidatingList: (listId: string) => void;
}

const ListContext = createContext<ListContextType | undefined>(undefined);

export const useListContext = () => {
    const context = useContext(ListContext);
    if (!context) {
        throw new Error("useListContext must be used within a ListProvider");
    }
    return context;
};

export const ListProvider = ({ children }: { children: React.ReactNode }) => {
    const [revalidatingLists, setRevalidatingLists] = useState<Set<string>>(new Set());

    const addRevalidatingList = useCallback((listId: string) => {
        setRevalidatingLists((prev) => new Set(prev).add(listId));
    }, []);

    const clearRevalidatingList = useCallback((listId: string) => {
        setRevalidatingLists((prev) => {
            const newSet = new Set(prev);
            newSet.delete(listId);
            return newSet;
        });
    }, []);

    return (
        <ListContext.Provider 
            value={{ 
                revalidatingLists, 
                setRevalidatingLists, 
                addRevalidatingList, 
                clearRevalidatingList 
            }}
        >
            {children}
        </ListContext.Provider>
    );
};
